import { useCallback, useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import Portrait from '../Portrait'
import { ApiError } from '../../lib/api'
import { depthChart, formatHeight } from '../../lib/dossier'
import type { AdminRequest } from '../../lib/adminApi'
import type { AdminPlayer, AdminPlayerResponse, AdminPlayersResponse } from '../../types/protocol'

// The players table stores Sleeper's "DEF"; rosters and the draft UI call the
// same thing DST. This screen is a view of the table, so it uses the raw value.
const POSITIONS = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'] as const
const STATUSES = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
] as const

const UNRANKED = 9_999_999

interface Props {
  request: AdminRequest
  onChanged: () => void
}

interface PlayerForm {
  full_name: string
  position: string
  nfl_team: string
  search_rank: string
}

const EMPTY_FORM: PlayerForm = { full_name: '', position: 'WR', nfl_team: '', search_rank: '' }

export default function PlayerTable({ request, onChanged }: Props) {
  const [query, setQuery] = useState('')
  const [position, setPosition] = useState('')
  const [status, setStatus] = useState<string>('all')
  const [page, setPage] = useState(1)
  const [data, setData] = useState<AdminPlayersResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState<PlayerForm>(EMPTY_FORM)
  const [newPlayer, setNewPlayer] = useState<PlayerForm>(EMPTY_FORM)
  const [busy, setBusy] = useState(false)

  const load = useCallback(
    async (signal?: { cancelled: boolean }) => {
      setLoading(true)
      const params = new URLSearchParams({ status, page: String(page) })
      if (query.trim()) params.set('q', query.trim())
      if (position) params.set('position', position)
      try {
        const response = await request<AdminPlayersResponse>(`/admin/players?${params}`)
        if (signal?.cancelled) return
        setData(response)
        setError(null)
      } catch (e) {
        if (signal?.cancelled) return
        setError(e instanceof ApiError ? e.message : 'Could not load players')
      } finally {
        if (!signal?.cancelled) setLoading(false)
      }
    },
    [request, query, position, status, page],
  )

  // Typing in the search box re-queries; a short debounce keeps a full name
  // from firing one request per keystroke.
  useEffect(() => {
    const signal = { cancelled: false }
    const timer = setTimeout(() => void load(signal), 250)
    return () => {
      signal.cancelled = true
      clearTimeout(timer)
    }
  }, [load])

  async function mutate(work: () => Promise<AdminPlayerResponse>, message: (player: AdminPlayer) => string) {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const { player } = await work()
      setNotice(message(player))
      setEditingId(null)
      await load()
      onChanged()
      return true
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save the change')
      return false
    } finally {
      setBusy(false)
    }
  }

  function startEdit(player: AdminPlayer) {
    setEditingId(player.id)
    setEditForm({
      full_name: player.full_name,
      position: player.position,
      nfl_team: player.nfl_team ?? '',
      search_rank: player.search_rank === UNRANKED ? '' : String(player.search_rank),
    })
  }

  async function saveEdit(event: FormEvent) {
    event.preventDefault()
    if (editingId === null) return
    await mutate(
      () =>
        request<AdminPlayerResponse>(`/admin/players/${editingId}`, {
          method: 'PATCH',
          body: bodyFrom(editForm),
        }),
      (player) => `Saved ${player.full_name}. Locked, so the next sync won't overwrite it.`,
    )
  }

  async function addPlayer(event: FormEvent) {
    event.preventDefault()
    if (!newPlayer.full_name.trim()) return
    const added = await mutate(
      () => request<AdminPlayerResponse>('/admin/players', { method: 'POST', body: bodyFrom(newPlayer) }),
      (player) => `Added ${player.full_name} (${player.position}). Locked, so syncs leave it alone.`,
    )
    if (added) setNewPlayer(EMPTY_FORM)
  }

  function toggle(player: AdminPlayer, field: 'active' | 'locked') {
    const next = !player[field]
    void mutate(
      () =>
        request<AdminPlayerResponse>(`/admin/players/${player.id}`, {
          method: 'PATCH',
          body: { [field]: next },
        }),
      (saved) =>
        field === 'active'
          ? `${saved.full_name} is now ${saved.active ? 'in' : 'out of'} the draft pool` +
            `${saved.locked ? ', and locked so the next sync keeps it that way.' : '.'}`
          : `${saved.full_name} is ${saved.locked ? 'locked against syncs' : 'unlocked'}.`,
    )
  }

  const pageCount = data?.page_count ?? 1

  return (
    <section className="space-y-4">
      <div className="panel p-6">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex-1 text-xs uppercase tracking-wide text-ink-lo">
            Search
            <input
              value={query}
              onChange={(event) => {
                setPage(1)
                setQuery(event.target.value)
              }}
              placeholder="Player name"
              className="mt-1 w-full rounded border border-line-strong bg-ink px-3 py-2 text-sm normal-case tracking-normal text-ink-hi"
            />
          </label>
          <label className="text-xs uppercase tracking-wide text-ink-lo">
            Position
            <select
              value={position}
              onChange={(event) => {
                setPage(1)
                setPosition(event.target.value)
              }}
              className="mt-1 block rounded border border-line-strong bg-ink px-3 py-2 text-sm normal-case tracking-normal text-ink-hi"
            >
              <option value="">Any</option>
              {POSITIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs uppercase tracking-wide text-ink-lo">
            Status
            <select
              value={status}
              onChange={(event) => {
                setPage(1)
                setStatus(event.target.value)
              }}
              className="mt-1 block rounded border border-line-strong bg-ink px-3 py-2 text-sm normal-case tracking-normal text-ink-hi"
            >
              {STATUSES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {notice && <p className="mt-3 text-sm text-signal">{notice}</p>}
        {error && <p className="mt-3 text-sm text-crit">{error}</p>}

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-ink-lo">
              <tr>
                <th className="py-1 pr-3 font-medium">Rank</th>
                <th className="py-1 pr-3 font-medium">Name</th>
                <th className="py-1 pr-3 font-medium">Pos</th>
                <th className="py-1 pr-3 font-medium">Team</th>
                <th className="py-1 pr-3 font-medium">#</th>
                <th className="py-1 pr-3 font-medium">Bye</th>
                <th className="py-1 pr-3 font-medium">Exp</th>
                <th className="py-1 pr-3 font-medium">Depth</th>
                <th className="py-1 pr-3 font-medium">Ht</th>
                <th className="py-1 pr-3 font-medium">Wt</th>
                <th className="py-1 pr-3 font-medium">Age</th>
                <th className="py-1 pr-3 font-medium">College</th>
                <th className="py-1 pr-3 font-medium">Flags</th>
                <th className="py-1 font-medium" />
              </tr>
            </thead>
            <tbody>
              {data?.players.map((player) =>
                editingId === player.id ? (
                  <tr key={player.id} className="border-t border-signal/40">
                    <td colSpan={14} className="py-2">
                      <form onSubmit={(e) => void saveEdit(e)} className="flex flex-wrap items-center gap-2">
                        <PlayerFormFields value={editForm} onChange={setEditForm} />
                        <button
                          type="submit"
                          disabled={busy || !editForm.full_name.trim()}
                          className="rounded bg-signal px-3 py-1.5 text-sm font-semibold text-ink disabled:opacity-50"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingId(null)}
                          className="rounded border border-line-strong px-3 py-1.5 text-sm"
                        >
                          Cancel
                        </button>
                      </form>
                    </td>
                  </tr>
                ) : (
                  <tr key={player.id} className="border-t border-line">
                    <td className="py-1.5 pr-3 font-mono text-xs text-ink-lo">
                      {player.search_rank === UNRANKED ? '—' : player.search_rank}
                    </td>
                    <td className="py-1.5 pr-3">
                      {/* The portrait rides in the name cell rather than a
                          column of its own, so the edit row's colSpan and the
                          header stay as they are. */}
                      <span className="flex items-center gap-2">
                        <Portrait
                          fullName={player.full_name}
                          position={player.position}
                          nflTeam={player.nfl_team}
                          src={player.portrait_url}
                          size="xs"
                        />
                        <span
                          className={
                            player.active ? 'text-ink-hi' : 'text-ink-lo line-through'
                          }
                        >
                          {player.full_name}
                        </span>
                      </span>
                    </td>
                    <td className="py-1.5 pr-3 text-ink-mid">{player.position}</td>
                    <td className="py-1.5 pr-3 text-ink-mid">{player.nfl_team ?? '—'}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs text-ink-mid">
                      {player.jersey_number ?? '—'}
                    </td>
                    {/* Sync-derived and read-only, like every other profile
                        column here — there is no admin edit for a bye. */}
                    <td className="py-1.5 pr-3 font-mono text-xs text-ink-mid">
                      {player.bye_week ?? '—'}
                    </td>
                    <td className="py-1.5 pr-3 text-ink-mid">{player.years_exp ?? '—'}</td>
                    {/* The em dash is an admin-only decision: this table prints
                        placeholders for missing data on purpose, where every
                        player-facing surface omits the field entirely. */}
                    <td className="py-1.5 pr-3 whitespace-nowrap text-ink-mid">
                      {depthChart(player.depth_chart_position, player.depth_chart_order) ?? '—'}
                    </td>
                    <td className="py-1.5 pr-3 whitespace-nowrap text-ink-mid">
                      {formatHeight(player.height_inches) ?? '—'}
                    </td>
                    <td className="py-1.5 pr-3 whitespace-nowrap text-ink-mid">
                      {player.weight_lbs === null ? '—' : `${player.weight_lbs} lbs`}
                    </td>
                    <td className="py-1.5 pr-3 text-ink-mid">{player.age ?? '—'}</td>
                    <td className="py-1.5 pr-3 text-ink-mid">{player.college ?? '—'}</td>
                    <td className="py-1.5 pr-3">
                      <div className="flex flex-wrap gap-1">
                        {player.injury_status && (
                          <span
                            title="Sleeper injury status"
                            className="rounded-full bg-crit/10 px-2 py-0.5 text-xs font-medium text-crit"
                          >
                            {player.injury_status}
                          </span>
                        )}
                        {player.roster_status && (
                          <span
                            title="Sleeper roster status"
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                              player.roster_status === 'Active'
                                ? 'bg-surface-3 text-ink-mid'
                                : 'bg-warn/10 text-warn'
                            }`}
                          >
                            {player.roster_status}
                          </span>
                        )}
                        {player.locked && (
                          <span
                            title="Kept through syncs — SleeperSync skips this row"
                            className="rounded-full bg-warn/10 px-2 py-0.5 text-xs font-medium text-warn"
                          >
                            Locked
                          </span>
                        )}
                        {player.manual && (
                          <span className="rounded-full bg-signal/10 px-2 py-0.5 text-xs font-medium text-signal">
                            Manual
                          </span>
                        )}
                        {!player.active && (
                          <span className="rounded-full bg-surface-3 px-2 py-0.5 text-xs text-ink-mid">
                            Inactive
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-1.5 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => startEdit(player)}
                        disabled={busy}
                        className="text-xs text-ink-mid underline-offset-2 hover:underline disabled:opacity-50"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => toggle(player, 'active')}
                        disabled={busy}
                        className="ml-3 text-xs text-ink-mid underline-offset-2 hover:underline disabled:opacity-50"
                      >
                        {player.active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggle(player, 'locked')}
                        disabled={busy}
                        className="ml-3 text-xs text-ink-mid underline-offset-2 hover:underline disabled:opacity-50"
                      >
                        {player.locked ? 'Unlock' : 'Lock'}
                      </button>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>

        {loading && <p className="mt-3 text-sm text-ink-lo">Loading…</p>}
        {!loading && data?.players.length === 0 && (
          <p className="mt-3 text-sm text-ink-lo">No players match those filters.</p>
        )}

        <div className="mt-4 flex items-center justify-between text-sm text-ink-mid">
          <span>
            {data ? `${data.total_count} players` : ''}
            {data && data.total_count > 0 ? ` · page ${data.page} of ${pageCount}` : ''}
          </span>
          <span className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1}
              className="rounded border border-line-strong px-3 py-1 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
              disabled={page >= pageCount}
              className="rounded border border-line-strong px-3 py-1 disabled:opacity-40"
            >
              Next
            </button>
          </span>
        </div>
      </div>

      <form onSubmit={(e) => void addPlayer(e)} className="panel p-6">
        <h2 className="eyebrow">Add a player</h2>
        <p className="mt-1 text-sm text-ink-mid">
          For anyone Sleeper doesn&apos;t list yet. Added players are locked, so the next sync
          leaves them alone.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <PlayerFormFields value={newPlayer} onChange={setNewPlayer} />
          <button
            type="submit"
            disabled={busy || !newPlayer.full_name.trim()}
            className="rounded bg-signal px-4 py-2 text-sm font-semibold text-ink hover:bg-signal-dim disabled:opacity-50"
          >
            Add player
          </button>
        </div>
      </form>
    </section>
  )
}

interface FieldsProps {
  value: PlayerForm
  onChange: (form: PlayerForm) => void
}

function PlayerFormFields({ value, onChange }: FieldsProps) {
  return (
    <>
      <input
        value={value.full_name}
        onChange={(event) => onChange({ ...value, full_name: event.target.value })}
        placeholder="Full name"
        className="min-w-48 flex-1 rounded border border-line-strong bg-ink px-2 py-1.5 text-sm"
      />
      <select
        value={value.position}
        onChange={(event) => onChange({ ...value, position: event.target.value })}
        className="rounded border border-line-strong bg-ink px-2 py-1.5 text-sm"
      >
        {POSITIONS.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <input
        value={value.nfl_team}
        onChange={(event) => onChange({ ...value, nfl_team: event.target.value })}
        placeholder="Team"
        maxLength={4}
        className="w-20 rounded border border-line-strong bg-ink px-2 py-1.5 text-sm uppercase"
      />
      <input
        value={value.search_rank}
        onChange={(event) => onChange({ ...value, search_rank: event.target.value })}
        placeholder="Rank"
        inputMode="numeric"
        className="w-24 rounded border border-line-strong bg-ink px-2 py-1.5 text-sm"
      />
    </>
  )
}

function bodyFrom(form: PlayerForm) {
  return {
    full_name: form.full_name.trim(),
    position: form.position,
    nfl_team: form.nfl_team.trim(),
    search_rank: form.search_rank.trim(),
  }
}
